import * as React from 'react'; // https://github.com/aelbore/esbuild-jest/issues/61
import Serializer, {
  type DeserializeArguments,
  type SerializeArguments,
} from './Serializer';
import EditorUtils from '../../AhaEditorUtils/EditorUtils';
import { type TableCellElement } from '../../types/aha-editor';
import wrapNonBlocksInContainer from '../helpers/wrapNonBlocksInContainer';
import {
  getBackgroundColor,
  getColspan,
  getRowspan,
  getVerticalAlign,
  getWidthOfCell,
  isHeaderCell,
} from '../helpers/tableHelpers';
import AlignmentUtils from '../../plugins/AlignmentPlugin/AlignmentUtils';
import { isTableCellElement } from '../../types/guards';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import classnames from 'classnames';

class SerializerTableCell extends Serializer {
  static serialize({ node, children, path, context }: SerializeArguments) {
    if (!isTableCellElement(node)) return;

    const style: React.CSSProperties = {};

    const width = getWidthOfCell(context.editor, path);
    if (width) {
      style.width = `${width}%`;
    }

    const verticalAlignment = node.table_cellVerticalAlign;
    if (verticalAlignment) {
      style.verticalAlign = verticalAlignment;
    }

    const cellColor = node.table_cellBackgroundColor;
    if (cellColor) {
      style.backgroundColor = cellColor;
    }

    // If rowSpan and colSpan are camelcase properties (for React) the
    // resulting HTML includes a lowercase rowspan (correct) but colspan
    // remains camel case (incorrect) which means tests fail. Unsure why
    // this is happening so for now, we'll set to lowercase for both.

    const data: Partial<HTMLTableCellElement> = {};
    if (node.table_rowspan) {
      // @ts-expect-error See explanation in comment above
      data.rowspan = node.table_rowspan;
    }
    if (node.table_colspan) {
      // @ts-expect-error See explanation in comment above
      data.colspan = node.table_colspan;
    }

    const Tag = isHeaderCell(context.editor, path) ? 'th' : 'td';

    return (
      <Tag
        // @ts-expect-error Unsure why this could be CSSStyleDeclaration
        style={style}
        {...data}
        className={classnames(`Editor__${Tag}`, data.className)}
      >
        {children}
      </Tag>
    );
  }

  static deserialize({
    el,
    next,
    markAttributes,
    context,
  }: DeserializeArguments): TableCellElement | undefined {
    if (
      !isHTMLElementNode(el) ||
      !['th', 'td'].includes(el.tagName.toLowerCase())
    ) {
      return;
    }

    if (!context.editor) return;

    const data: Omit<TableCellElement, 'type' | 'children'> = {};

    const rowSpan = getRowspan(el);
    if (rowSpan && rowSpan >= 1) {
      data.table_rowspan = rowSpan;
    }

    const colSpan = getColspan(el);
    if (colSpan && colSpan >= 1) {
      data.table_colspan = colSpan;
    }

    const backgroundColor = getBackgroundColor(el);
    if (backgroundColor) {
      data.table_cellBackgroundColor = backgroundColor;
    }

    const verticalAlign = getVerticalAlign(el);
    if (verticalAlign) {
      data.table_cellVerticalAlign = verticalAlign;
    }

    const alignment = AlignmentUtils.alignmentDataFromHtmlElement(el);
    if (alignment) {
      // If alignment data is on the table cell itself, we should move
      // it to the child nodes, because we only support alignment on
      // specific block nodes.
      const alignmentClass = AlignmentUtils.alignmentClassForValue(alignment);

      if (alignmentClass) {
        // el.children contains child elements only, not text nodes, so
        // this code will run if a table cell contains only text, not
        // wrapped in an inner block like a paragraph.
        if (!el.children || el.children.length === 0) {
          const clonedEl = el.cloneNode() as HTMLTableCellElement;
          // Have to use innerHTML here, because if we're on the server,
          // we may not have document in scope to call
          // document.createElement.
          clonedEl.innerHTML = '<p />';
          const wrapper = clonedEl.firstElementChild;
          if (wrapper) {
            // This will move any text nodes that were direct children of
            // the table cell into the paragraph.
            while (el.firstChild) {
              wrapper.appendChild(el.firstChild);
            }
            el.appendChild(wrapper);
          }
          clonedEl.remove();
        }

        for (const child of el.children) {
          child.classList.add(alignmentClass);
        }
      }
    }

    const children = wrapNonBlocksInContainer(
      context.editor,
      next(el.childNodes, markAttributes),
      'paragraph'
    );

    if (children.length === 0) {
      children.push(EditorUtils.emptyParagraph());
    }

    return {
      type: 'table-cell',
      ...data,
      children,
    };
  }
}

export default SerializerTableCell;
